import { useEffect, useRef } from "react";
import { useCreateMap } from "../../integration/useCreateMap";
import { createRoot } from "react-dom/client";
import mqtt from "mqtt";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const nlfContainer = document.createElement("div");
nlfContainer.setAttribute("id", "nlf-pin-root");
document.body.appendChild(nlfContainer);
const domNode = document.getElementById("nlf-pin-root");
const root = createRoot(domNode!);
root.render(
  <LocalShippingIcon
    fontSize={"large"}
    style={{
      transform: "translate(-50%, -50%)"
    }}
  />
);
document.body.removeChild(nlfContainer);
nlfContainer.removeAttribute("id");

const NlfMapPage = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const hMap = useRef<H.Map | undefined>(undefined);
  const markerRef = useRef<H.map.DomMarker | null>(null);
  const ui = useRef<H.ui.UI | null | undefined>(undefined);
  const createMap = useCreateMap(false);

  const pins: {
    date: string;
    text: string;
    location: { lat: number; lon: number };
  }[] = [
    {
      date: "23.–27. juli",
      text: "Seljord Country Festival",
      location: {
        lat: 59.488338,
        lon: 8.62022,
      },
    },
    {
      date: "28. juli",
      text: "Bergen Eikås motorsenter",
      location: {
        lat: 60.391262,
        lon: 5.322054,
      },
    },
    {
      date: "31. juli – 2. aug",
      text: "Førde Trøkkn Truck",
      location: {
        lat: 61.451931,
        lon: 5.84386,
      },
    },
    {
      date: "5. aug",
      text: "Ålesund, Wist Last & Buss AS",
      location: {
        lat: 62.472229,
        lon: 6.149482,
      },
    },
    {
      date: "6. aug",
      text: "Molde Wist Last Buss",
      location: {
        lat: 62.737709,
        lon: 7.16091,
      },
    },
    {
      date: "8.–9. aug",
      text: "Trondheim Solsiden",
      location: {
        lat: 63.430515,
        lon: 10.395053,
      },
    },
    {
      date: "11. aug",
      text: "Alta Alta VGS",
      location: {
        lat: 69.968086,
        lon: 23.271521,
      },
    },
    {
      date: "12. aug",
      text: "Tromsø Tivolitomta",
      location: {
        lat: 69.649208,
        lon: 18.955324,
      },
    },
    {
      date: "14. aug",
      text: "Harstad Amfi Senteret",
      location: {
        lat: 68.797997,
        lon: 16.53285,
      },
    },
    {
      date: "16. aug",
      text: "Bodø Nordlandshallen",
      location: {
        lat: 67.279999,
        lon: 14.40501,
      },
    },
    {
      date: "18. aug",
      text: "Gjøvik, Opplæringskontoret",
      location: {
        lat: 60.8721617,
        lon: 10.5038864,
      },
    },
    {
      date: "19. aug",
      text: "Løten Myklegaard",
      location: {
        lat: 60.820648,
        lon: 11.34293,
      },
    },
    {
      date: "20. aug",
      text: "Moelv og Hunndalen",
      location: {
        lat: 60.938229,
        lon: 10.69982,
      },
    },
    {
      date: "23. aug",
      text: "Sandnes Kvadrat Kjøpesenter",
      location: {
        lat: 58.85313,
        lon: 5.73271,
      },
    },
    {
      date: "26. aug",
      text: "Porsgrunn Rådhusplassen",
      location: {
        lat: 59.136509,
        lon: 9.65132,
      },
    },
    {
      date: "27. aug",
      text: "Notodden Konvoi Kongsberg – Notodden",
      location: {
        lat: 59.559269,
        lon: 9.25985,
      },
    },
    {
      date: "28. aug",
      text: "Fredrikstad",
      location: {
        lat: 59.220535,
        lon: 10.934701,
      },
    },
    {
      date: "30. aug",
      text: "Horten, Lastebilcruise",
      location: {
        lat: 59.412472,
        lon: 10.48553,
      },
    },
    {
      date: "1. sep",
      text: "Kløfta, Scania",
      location: {
        lat: 60.0753432,
        lon: 11.1391499,
      },
    },
    {
      date: "12. sep",
      text: "Seljord Dyrskun",
      location: {
        lat: 59.488338,
        lon: 8.62022,
      },
    },
  ];

  // Setup flespi
  useEffect(() => {
    const prodFlespiToken =
      "dnm1jiL6kk426WfVEGWO5wcJK3k6eALzqPjP9aogaOKWFLfZQZluWiqisKUo5TIL";

    const prodDeviceId = "6522864";

    const mqttConnection = mqtt.connect("mqtts://mqtt.flespi.io/", {
      username: prodFlespiToken,
      password: "",
    });

    const mainTopic = `flespi/state/gw/devices/${prodDeviceId}/telemetry/position`;
    // const deviceMessagesTopic = `flespi/message/gw/devices/${device}`;
    mqttConnection.on("connect", function () {
      console.log("connected to mqtt.flespi.io");
      mqttConnection.subscribe(mainTopic, (err: any) => {
        if (err) {
          console.error(err, "Error subscribing", { topic: mainTopic });
        }
      });
    });

    mqttConnection.on("message", async (topic: any, rawMessage: any) => {
      const message = JSON.parse(rawMessage.toString());
      // console.log("Device message", { topic, message });
      if (message.latitude && message.longitude) {
        if (markerRef.current && hMap.current) {
          // console.log("setting geometry", message);
          markerRef.current.setGeometry(
            new H.geo.Point(message.latitude, message.longitude)
          );
        }
      }
    });

    return () => {
      mqttConnection.end();
    };
  }, []);

  // Setup map
  useEffect(() => {
    // Get the default map types from the platform object
    const mapDiv = mapRef.current;
    const { map, mapUi } = createMap(mapDiv!);
    hMap.current = map;

    const behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(map));

    // Set fixed bounds to overlook Norway
    const norwayBounds = new H.geo.Rect(58.0, 4.0, 71.5, 25.0); // South-West and North-East corners of Norway
    map.getViewModel().setLookAtData({ bounds: norwayBounds });

    for (const pin of pins) {
      const markerElement = document.createElement("div");
      markerElement.style.position = "absolute";
      markerElement.style.padding = "5px 10px";
      markerElement.style.backgroundColor = "#fff8d4"; // Change to your desired background color
      markerElement.style.color = "#000000"; // Text color
      markerElement.style.borderRadius = "5px"; // Rounded corners
      markerElement.style.boxShadow = "0px 2px 5px rgba(0, 0, 0, 0.3)";
      markerElement.style.whiteSpace = "nowrap"; // Prevent text from wrapping
      markerElement.textContent = `${pin.date} ${pin.text}`; // Set the marker's text

      const marker = new H.map.DomMarker(
        { lat: pin.location.lat, lng: pin.location.lon },
        {
          icon: new H.map.DomIcon(markerElement),
          data: `${pin.date} ${pin.text}`,
          visibility: true,
        }
      );
      hMap.current.addObject(marker);
    }

    const marker = new H.map.DomMarker(
      { lat: 0, lng: 0 },
      {
        icon: new H.map.DomIcon(nlfContainer),
        data: "",
        visibility: true,
      }
    );
    marker.setZIndex(5000);
    markerRef.current = marker;

    hMap.current.addObject(marker);

    ui.current = mapUi;

    const resize = () => {
      map.getViewPort().resize();
    };
    window.addEventListener("resize", resize);

    return () => {
      // Remove old map if this rerenders (this happens on save file while developing)
      while (mapDiv?.firstChild) {
        mapDiv.removeChild(mapDiv.firstChild);
      }
    };
  }, [createMap]);

  return <div style={{ height: "100vh", width: "100vw" }} ref={mapRef} />;
};

export default NlfMapPage;
